import React, {
  ChangeEvent,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Card from "components/ui/Card/Card";
import Alert from "components/ui/Alert/Alert";
import Spinner from "components/ui/Spinner/Spinner";
import ConfirmDialog from "components/ui/ConfirmDialog/ConfirmDialog";
import {
  fetchAccountDetail,
  fetchAccountLookups,
  createAccount,
  updateAccount,
  deleteAccount,
  type AccountDetailDto,
  type LookupOptionDto,
  type UpazilaOptionDto,
  type AddressDto,
  type AccountSavePayload,
} from "utils/accountsApi";
import { useI18n } from "context/I18nContext";
import { ApiError } from "utils/apiClient";
import {
  formatDetailLabel,
  getDetailEntries,
  normaliseErrorDetail,
} from "utils/errorDetails";
import styles from "./AccountEditPage.module.scss";

const ADDRESS_TYPE_PRESENT = 1;
const ADDRESS_TYPE_PERMANENT = 2;

type LookupOption = { id: number; name: string };
type UpazilaOption = LookupOption & { districtId: number };

type AddressFormState = {
  flatNo: string;
  houseNo: string;
  roadNo: string;
  villageName: string;
  postCode: string;
  districtId: string;
  upazilaId: string;
};

type AccountFormState = {
  accountNo: string;
  walletBalance: string;
  statusId: string;
};

type CustomerFormState = {
  customerName: string;
  mobileNo: string;
  identityTypeId: string;
  identityNo: string;
  dateOfBirth: string;
  genderId: string;
  fatherName: string;
  motherName: string;
  occupationId: string;
  pumpId: string;
  statusId: string;
};

type FormState = {
  account: AccountFormState;
  customer: CustomerFormState;
  presentAddress: AddressFormState;
  permanentAddress: AddressFormState;
  sameAsPresent: boolean;
};

type LookupCollections = {
  statuses: LookupOption[];
  identityTypes: LookupOption[];
  genders: LookupOption[];
  occupations: LookupOption[];
  pumps: LookupOption[];
  districts: LookupOption[];
  upazilas: UpazilaOption[];
};

type AccountFormMode = "create" | "edit";

type AccountFormPageProps = {
  mode: AccountFormMode;
};

type CustomerFieldErrors = Partial<Record<"customerName" | "mobileNo", string>>;

type RouteState = {
  from?: {
    pathname?: string;
    search?: string;
  };
};

const createEmptyAddress = (): AddressFormState => ({
  flatNo: "",
  houseNo: "",
  roadNo: "",
  villageName: "",
  postCode: "",
  districtId: "",
  upazilaId: "",
});

const createEmptyFormState = (): FormState => ({
  account: {
    accountNo: "",
    walletBalance: "",
    statusId: "",
  },
  customer: {
    customerName: "",
    mobileNo: "",
    identityTypeId: "",
    identityNo: "",
    dateOfBirth: "",
    genderId: "",
    fatherName: "",
    motherName: "",
    occupationId: "",
    pumpId: "",
    statusId: "",
  },
  presentAddress: createEmptyAddress(),
  permanentAddress: createEmptyAddress(),
  sameAsPresent: false,
});

const normaliseLookupOptions = (options?: LookupOptionDto[]): LookupOption[] =>
  options?.map(({ id, name }) => ({ id, name })) ?? [];

const normaliseUpazilas = (options?: UpazilaOptionDto[]): UpazilaOption[] =>
  options?.map(({ id, name, districtId }) => ({ id, name, districtId })) ?? [];

const toLookupCollections = (
  source?: AccountDetailDto["lookups"],
): LookupCollections => ({
  statuses: normaliseLookupOptions(source?.statuses),
  identityTypes: normaliseLookupOptions(source?.identityTypes),
  genders: normaliseLookupOptions(source?.genders),
  occupations: normaliseLookupOptions(source?.occupations),
  pumps: normaliseLookupOptions(source?.pumps),
  districts: normaliseLookupOptions(source?.districts),
  upazilas: normaliseUpazilas(source?.upazilas),
});

const toAddressFormState = (address?: AddressDto): AddressFormState => ({
  flatNo: address?.flatNo ?? "",
  houseNo: address?.houseNo ?? "",
  roadNo: address?.roadNo ?? "",
  villageName: address?.villageName ?? "",
  postCode: address?.postCode ?? "",
  districtId:
    address?.districtId !== undefined && address?.districtId !== null
      ? String(address.districtId)
      : "",
  upazilaId:
    address?.upazilaId !== undefined && address?.upazilaId !== null
      ? String(address.upazilaId)
      : "",
});

const addressesEqual = (a: AddressDto | undefined, b: AddressDto | undefined) => {
  if (!a || !b) {
    return false;
  }

  return (
    (a.flatNo ?? "") === (b.flatNo ?? "") &&
    (a.houseNo ?? "") === (b.houseNo ?? "") &&
    (a.roadNo ?? "") === (b.roadNo ?? "") &&
    (a.villageName ?? "") === (b.villageName ?? "") &&
    (a.postCode ?? "") === (b.postCode ?? "") &&
    (a.districtId ?? "") === (b.districtId ?? "") &&
    (a.upazilaId ?? "") === (b.upazilaId ?? "")
  );
};

const mapDetailToFormState = (detail: AccountDetailDto): FormState => {
  const presentAddressDto = detail.addresses.find(
    (address) => address.addressTypeId === ADDRESS_TYPE_PRESENT
  );
  const permanentAddressDto = detail.addresses.find(
    (address) => address.addressTypeId === ADDRESS_TYPE_PERMANENT
  );

  const sameAsPresent = addressesEqual(presentAddressDto, permanentAddressDto);

  return {
    account: {
      accountNo: detail.account.accountNo ?? "",
      walletBalance: detail.account.walletBalance?.toString() ?? "",
      statusId: detail.account.statusId?.toString() ?? "",
    },
    customer: {
      customerName: detail.customer.customerName ?? "",
      mobileNo: detail.customer.mobileNo ?? "",
      identityTypeId: detail.customer.identityTypeId?.toString() ?? "",
      identityNo: detail.customer.identityNo ?? "",
      dateOfBirth: detail.customer.dateOfBirth ?? "",
      genderId: detail.customer.genderId?.toString() ?? "",
      fatherName: detail.customer.fatherName ?? "",
      motherName: detail.customer.motherName ?? "",
      occupationId: detail.customer.occupationId?.toString() ?? "",
      pumpId: detail.customer.pumpId?.toString() ?? "",
      statusId: detail.customer.statusId?.toString() ?? "",
    },
    presentAddress: toAddressFormState(presentAddressDto),
    permanentAddress: sameAsPresent
      ? toAddressFormState(presentAddressDto)
      : toAddressFormState(permanentAddressDto),
    sameAsPresent,
  };
};

const optionalString = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

const optionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

const buildSavePayload = (
  state: FormState,
  options?: { includeAccountNo?: boolean }
): AccountSavePayload => {
  const includeAccountNo = options?.includeAccountNo !== false;
  const trimmedAccountNo = state.account.accountNo.trim();

  const buildAddressPayload = (addressTypeId: number, address: AddressFormState) => ({
    addressTypeId,
    flatNo: optionalString(address.flatNo),
    houseNo: optionalString(address.houseNo),
    roadNo: optionalString(address.roadNo),
    villageName: optionalString(address.villageName),
    postCode: optionalString(address.postCode),
    districtId: optionalNumber(address.districtId) ?? undefined,
    upazilaId: optionalNumber(address.upazilaId) ?? undefined,
  });

  const hasAddressContent = (payload: {
    flatNo: string | null;
    houseNo: string | null;
    roadNo: string | null;
    villageName: string | null;
    postCode: string | null;
    districtId?: number;
    upazilaId?: number;
  }) =>
    [
      payload.flatNo,
      payload.houseNo,
      payload.roadNo,
      payload.villageName,
      payload.postCode,
      payload.districtId,
      payload.upazilaId,
    ].some((value) => value !== null && value !== undefined);

  const presentPayload = buildAddressPayload(ADDRESS_TYPE_PRESENT, state.presentAddress);
  const permanentPayload = buildAddressPayload(
    ADDRESS_TYPE_PERMANENT,
    state.permanentAddress
  );

  const addresses = [presentPayload, permanentPayload].filter((payload) =>
    hasAddressContent(payload)
  );

  const accountPayload: AccountSavePayload["account"] = {
    walletBalance: Number(state.account.walletBalance || 0),
    statusId: Number(state.account.statusId || 0),
  };

  if (includeAccountNo && trimmedAccountNo) {
    accountPayload.accountNo = trimmedAccountNo;
  }

  return {
    account: accountPayload,
    customer: {
      customerName: state.customer.customerName.trim(),
      mobileNo: optionalString(state.customer.mobileNo),
      identityTypeId: optionalNumber(state.customer.identityTypeId) ?? undefined,
      identityNo: optionalString(state.customer.identityNo),
      dateOfBirth: optionalString(state.customer.dateOfBirth) ?? undefined,
      genderId: optionalNumber(state.customer.genderId) ?? undefined,
      fatherName: optionalString(state.customer.fatherName),
      motherName: optionalString(state.customer.motherName),
      occupationId: optionalNumber(state.customer.occupationId) ?? undefined,
      pumpId: optionalNumber(state.customer.pumpId) ?? undefined,
      statusId: Number(state.customer.statusId || 0),
    },
    addresses,
  };
};

function AccountFormPage({ mode }: AccountFormPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = mode === "edit";
  const { t } = useI18n();
  const routeState = location.state as RouteState | undefined;
  const backTarget = useMemo(
    () => ({
      pathname: routeState?.from?.pathname ?? "/accounts",
      search: routeState?.from?.search ?? "",
    }),
    [routeState?.from?.pathname, routeState?.from?.search]
  );

  const [formState, setFormState] = useState<FormState>(createEmptyFormState);
  const [lookups, setLookups] = useState<LookupCollections>({
    statuses: [],
    identityTypes: [],
    genders: [],
    occupations: [],
    pumps: [],
    districts: [],
    upazilas: [],
  });
  const [loading, setLoading] = useState(isEditMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadDetail, setLoadDetail] = useState<Record<string, unknown> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveDetail, setSaveDetail] = useState<Record<string, unknown> | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [postSaveLock, setPostSaveLock] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [customerFieldErrors, setCustomerFieldErrors] = useState<CustomerFieldErrors>({});
  const redirectTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!isEditMode) {
      setLoading(false);
      setLoadError(null);
      setLoadDetail(null);
      return;
    }

    if (!id) {
      setLoadError(t("accountForm.errors.missingId"));
      setLoadDetail(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setLoadDetail(null);
      try {
        const detail = await fetchAccountDetail(id, controller.signal, {
          includeLookups: true,
        });
        setFormState(mapDetailToFormState(detail));

        setLookups(toLookupCollections(detail.lookups));
        setLoadDetail(null);
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : (err as Error).message || t("accountForm.errors.loadFailed");
        setLoadDetail(
          err instanceof ApiError ? normaliseErrorDetail(err.detail) : null
        );
        setLoadError(message || t("accountForm.errors.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => controller.abort();
  }, [id, isEditMode, t]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }
    const controller = new AbortController();
    const loadLookups = async () => {
      try {
        const result = await fetchAccountLookups(controller.signal);
        setLookups(toLookupCollections(result));
      } catch {
        setLookups(toLookupCollections(undefined));
      }
    };
    loadLookups();
    return () => controller.abort();
  }, [isEditMode]);

  useEffect(() => {
    const defaultStatusId = lookups.statuses[0]?.id;
    if (!defaultStatusId) {
      return;
    }

    setFormState((previous) => {
      const shouldSetAccount = !previous.account.statusId;
      const shouldSetCustomer = !previous.customer.statusId;

      if (!shouldSetAccount && !shouldSetCustomer) {
        return previous;
      }

      let nextAccount = previous.account;
      let nextCustomer = previous.customer;

      if (shouldSetAccount) {
        nextAccount = {
          ...previous.account,
          statusId: String(defaultStatusId),
        };
      }

      if (shouldSetCustomer) {
        nextCustomer = {
          ...previous.customer,
          statusId: String(defaultStatusId),
        };
      }

      return {
        ...previous,
        account: nextAccount,
        customer: nextCustomer,
      };
    });
  }, [lookups.statuses]);

  const presentUpazilaOptions = useMemo(
    () =>
      lookups.upazilas.filter(
        (option) => option.districtId === Number(formState.presentAddress.districtId)
      ),
    [lookups.upazilas, formState.presentAddress.districtId]
  );

  const permanentUpazilaOptions = useMemo(
    () =>
      lookups.upazilas.filter(
        (option) => option.districtId === Number(formState.permanentAddress.districtId)
      ),
    [lookups.upazilas, formState.permanentAddress.districtId]
  );

  const isCustomerRequiredField = (
    field: string,
  ): field is keyof CustomerFieldErrors => field === "customerName" || field === "mobileNo";

  const clearCustomerFieldError = (field: keyof CustomerFieldErrors) => {
    setCustomerFieldErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const handleAccountChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormState((previous) => ({
      ...previous,
      account: { ...previous.account, [name]: value },
    }));
  };

  const handleCustomerChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    if (isCustomerRequiredField(name)) {
      clearCustomerFieldError(name);
    }
    setFormState((previous) => ({
      ...previous,
      customer: { ...previous.customer, [name]: value },
    }));
  };

  const updateAddress = (
    addressType: "presentAddress" | "permanentAddress",
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormState((previous) => {
      const updatedAddress = {
        ...previous[addressType],
        [name]: value,
      };

      const shouldMirror =
        addressType === "presentAddress" && previous.sameAsPresent;

      return {
        ...previous,
        presentAddress:
          addressType === "presentAddress"
            ? updatedAddress
            : previous.presentAddress,
        permanentAddress:
          addressType === "permanentAddress"
            ? updatedAddress
            : shouldMirror
              ? { ...updatedAddress }
              : previous.permanentAddress,
      };
    });
  };

  const handleSameAsPresentToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFormState((previous) => ({
      ...previous,
      sameAsPresent: checked,
      permanentAddress: checked
        ? { ...previous.presentAddress }
        : previous.permanentAddress,
    }));
  };

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(backTarget);
    }
  };

  const handleDelete = async () => {
    if (!id) {
      setDeleteError(t("accountForm.errors.missingId"));
      return;
    }

    setDeleteError(null);
    setDeleting(true);

    try {
      await deleteAccount(id);
      navigate(backTarget, {
        replace: true,
        state: {
              flash: {
                variant: "success",
                message: t("accountForm.flash.deleteSuccess"),
              },
            },
          });
    } catch (err) {
      setDeleteError((err as Error).message || t("accountForm.errors.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteConfirm = () => setConfirmDeleteOpen(true);
  const closeDeleteConfirm = () => {
    if (!deleting) {
      setConfirmDeleteOpen(false);
    }
  };
  const confirmDelete = () => {
    setConfirmDeleteOpen(false);
    handleDelete();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isEditMode && !id) {
      setSaveError(t("accountForm.errors.missingId"));
      return;
    }

    setSaveError(null);
    setSaveSuccess(null);
    setPostSaveLock(false);
    setSaveDetail(null);
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    const validationErrors: CustomerFieldErrors = {};
    if (!formState.customer.customerName.trim()) {
      validationErrors.customerName = t("accountForm.errors.customerNameRequired");
    }
    if (!formState.customer.mobileNo.trim()) {
      validationErrors.mobileNo = t("accountForm.errors.mobileNoRequired");
    }
    if (Object.keys(validationErrors).length > 0) {
      setCustomerFieldErrors(validationErrors);
      const firstErrorRef =
        validationErrors.customerName && customerNameRef.current
          ? customerNameRef
          : validationErrors.mobileNo && mobileNoRef.current
            ? mobileNoRef
            : null;
      if (firstErrorRef?.current) {
        firstErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        firstErrorRef.current.focus();
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    setCustomerFieldErrors({});
    setSaving(true);

    try {
      const payload = buildSavePayload(formState, { includeAccountNo: isEditMode });
      if (isEditMode && id) {
        await updateAccount(id, payload);
        setSaveSuccess(t("accountForm.messages.updateSuccess"));
        setSaveDetail(null);
      } else {
        await createAccount(payload);
        setSaveSuccess(t("accountForm.messages.createSuccessRedirect"));
        setSaveDetail(null);
        setPostSaveLock(true);
        const flashMessage = t("accountForm.flash.createSuccess");
        redirectTimerRef.current = window.setTimeout(() => {
          navigate(backTarget, {
            replace: true,
            state: {
              flash: {
                variant: "success",
                message: flashMessage,
              },
            },
          });
        }, 1800);
      }
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      const message =
        apiError?.message ||
        (err as Error).message ||
        t("accountForm.errors.saveFailed");
      setSaveDetail(normaliseErrorDetail(apiError?.detail));
      setSaveError(message);
      setPostSaveLock(false);
    } finally {
      setSaving(false);
    }
  };

  const customerNameRef = useRef<HTMLInputElement | null>(null);
  const mobileNoRef = useRef<HTMLInputElement | null>(null);
  const alertStackRef = useRef<HTMLDivElement | null>(null);

  const formId = isEditMode ? "account-edit" : "account-create";
  const pageTitle = isEditMode ? t("accountForm.titleEdit") : t("accountForm.titleCreate");
  const pageSubtitle = isEditMode
    ? t("accountForm.subtitleEdit")
    : t("accountForm.subtitleCreate");
  const primaryActionLabel = saving
    ? isEditMode
      ? t("accountForm.buttons.saving")
      : t("accountForm.buttons.creating")
    : isEditMode
      ? t("accountForm.buttons.save")
      : t("accountForm.buttons.create");
  const formDisabled = saving || loading || !!loadError || postSaveLock || deleting;
  const deleteButtonLabel = deleting
    ? t("accountForm.buttons.deleting")
    : t("accountForm.buttons.delete");
  const backLabel = t("accountForm.buttons.back");
  const cancelLabel = t("accountForm.buttons.cancel");
  const deleteConfirmMessage = t("accountForm.confirmDelete");

  useEffect(() => {
    if (!(loadError || saveError || saveSuccess || deleteError)) {
      return;
    }
    const element = alertStackRef.current;
    if (!element) {
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [loadError, saveError, saveSuccess, deleteError]);

  const renderDetail = (detail: Record<string, unknown> | null) => {
    const entries = getDetailEntries(detail);
    if (!entries.length) {
      return null;
    }
    return (
      <dl className={styles.detailList}>
        {entries.map(([key, value]) => (
          <div key={key} className={styles.detailRow}>
            <dt>{formatDetailLabel(key)}</dt>
            <dd>{String(value)}</dd>
          </div>
        ))}
      </dl>
    );
  };

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <div className={styles.intro}>
            <h1 className={styles.title}>{pageTitle}</h1>
            <p className={styles.subtitle}>{pageSubtitle}</p>
          </div>
          <button
            type="button"
            className={`${styles.backButton} ${styles.backButtonSmall}`}
            onClick={handleBackNavigation}
            disabled={saving || deleting}
          >
            {backLabel}
          </button>
        </div>

        {loading && (
          <div className={styles.loadingState}>
            <Spinner label={t("accountForm.loading")} />
          </div>
        )}

        {(loadError || saveError || saveSuccess || deleteError) && (
          <div ref={alertStackRef} className={styles.alertStack}>
            {loadError && (
              <Alert
                variant="error"
                dismissible
                onClose={() => setLoadError(null)}
              >
                <div>{loadError}</div>
                {renderDetail(loadDetail)}
              </Alert>
            )}
            {saveError && (
              <Alert
                variant="error"
                dismissible
                onClose={() => setSaveError(null)}
              >
                <div>{saveError}</div>
                {renderDetail(saveDetail)}
              </Alert>
            )}
            {deleteError && (
              <Alert
                variant="error"
                dismissible
                onClose={() => setDeleteError(null)}
              >
                {deleteError}
              </Alert>
            )}
            {saveSuccess && (
              <Alert
                variant="success"
                dismissible={!postSaveLock}
                onClose={
                  postSaveLock ? undefined : () => setSaveSuccess(null)
                }
              >
                {saveSuccess}
              </Alert>
            )}
          </div>
        )}

        <form id={formId} className={styles.form} onSubmit={handleSubmit}>
          <fieldset
            className={styles.fieldset}
            disabled={formDisabled}
          >
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t("accountForm.sections.account")}</h2>
              {isEditMode && (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="accountNo">
                    {t("accountForm.fields.accountNo")}
                  </label>
                  <input
                    id="accountNo"
                    name="accountNo"
                    className={styles.input}
                    value={formState.account.accountNo}
                    onChange={handleAccountChange}
                    placeholder={t("accountForm.placeholders.accountNo")}
                    disabled
                  />
                </div>
              )}
              <div className={styles.field}>
                <label className={styles.label} htmlFor="accountStatusId">
                  {t("accountForm.fields.accountStatus")}
                </label>
                <select
                  id="accountStatusId"
                  name="statusId"
                  className={styles.select}
                  value={formState.account.statusId}
                  onChange={handleAccountChange}
                >
                  <option value="">{t("accountForm.placeholders.selectStatus")}</option>
                  {lookups.statuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.divider} />
              <h3 className={styles.sectionSubtitle}>
                {t("accountForm.sections.customer")}
              </h3>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="customerName">
                  {t("accountForm.fields.customerName")}
                  <span className={styles.required} aria-hidden="true">*</span>
                </label>
                <input
                  ref={customerNameRef}
                  id="customerName"
                  name="customerName"
                  className={styles.input}
                  value={formState.customer.customerName}
                  onChange={handleCustomerChange}
                  placeholder={t("accountForm.placeholders.customerName")}
                />
                {customerFieldErrors.customerName && (
                  <span className={styles.fieldError} role="alert">
                    {customerFieldErrors.customerName}
                  </span>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="mobileNo">
                  {t("accountForm.fields.mobileNo")}
                  <span className={styles.required} aria-hidden="true">*</span>
                </label>
                <input
                  ref={mobileNoRef}
                  id="mobileNo"
                  name="mobileNo"
                  className={styles.input}
                  value={formState.customer.mobileNo}
                  onChange={handleCustomerChange}
                  placeholder={t("accountForm.placeholders.mobileNo")}
                />
                {customerFieldErrors.mobileNo && (
                  <span className={styles.fieldError} role="alert">
                    {customerFieldErrors.mobileNo}
                  </span>
                )}
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="identityTypeId">
                    {t("accountForm.fields.identityType")}
                  </label>
                  <select
                    id="identityTypeId"
                    name="identityTypeId"
                    className={styles.select}
                    value={formState.customer.identityTypeId}
                    onChange={handleCustomerChange}
                  >
                    <option value="">
                      {t("accountForm.placeholders.selectIdentityType")}
                    </option>
                    {lookups.identityTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="identityNo">
                    {t("accountForm.fields.identityNo")}
                  </label>
                  <input
                    id="identityNo"
                    name="identityNo"
                    className={styles.input}
                    value={formState.customer.identityNo}
                    onChange={handleCustomerChange}
                  />
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="dateOfBirth">
                    {t("accountForm.fields.dateOfBirth")}
                  </label>
                  <input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    className={styles.input}
                    value={formState.customer.dateOfBirth}
                    onChange={handleCustomerChange}
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="genderId">
                    {t("accountForm.fields.gender")}
                  </label>
                <select
                  id="genderId"
                  name="genderId"
                  className={styles.select}
                  value={formState.customer.genderId}
                  onChange={handleCustomerChange}
                >
                  <option value="">{t("accountForm.placeholders.selectGender")}</option>
                  {lookups.genders.map((gender) => (
                    <option key={gender.id} value={gender.id}>
                      {gender.name}
                    </option>
                  ))}
                  </select>
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="fatherName">
                    {t("accountForm.fields.fatherName")}
                  </label>
                  <input
                    id="fatherName"
                    name="fatherName"
                    className={styles.input}
                    value={formState.customer.fatherName}
                    onChange={handleCustomerChange}
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="motherName">
                    {t("accountForm.fields.motherName")}
                  </label>
                  <input
                    id="motherName"
                    name="motherName"
                    className={styles.input}
                    value={formState.customer.motherName}
                    onChange={handleCustomerChange}
                  />
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="occupationId">
                    {t("accountForm.fields.occupation")}
                  </label>
                  <select
                    id="occupationId"
                    name="occupationId"
                    className={styles.select}
                    value={formState.customer.occupationId}
                    onChange={handleCustomerChange}
                  >
                    <option value="">
                      {t("accountForm.placeholders.selectOccupation")}
                    </option>
                    {lookups.occupations.map((occupation) => (
                      <option key={occupation.id} value={occupation.id}>
                        {occupation.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="pumpId">
                    {t("accountForm.fields.pump")}
                  </label>
                  <select
                    id="pumpId"
                    name="pumpId"
                    className={styles.select}
                    value={formState.customer.pumpId}
                    onChange={handleCustomerChange}
                  >
                    <option value="">
                      {t("accountForm.placeholders.selectPump")}
                    </option>
                    {lookups.pumps.map((pump) => (
                      <option key={pump.id} value={pump.id}>
                        {pump.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="customerStatusId">
                  {t("accountForm.fields.customerStatus")}
                </label>
                <select
                  id="customerStatusId"
                  name="statusId"
                  className={styles.select}
                  value={formState.customer.statusId}
                  onChange={handleCustomerChange}
                >
                  <option value="">
                    {t("accountForm.placeholders.selectStatus")}
                  </option>
                  {lookups.statuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t("accountForm.sections.present")}</h2>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="presentFlatNo">
                  {t("accountForm.fields.flatNo")}
                </label>
                <input
                  id="presentFlatNo"
                  name="flatNo"
                  className={styles.input}
                  value={formState.presentAddress.flatNo}
                  onChange={(event) => updateAddress("presentAddress", event)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="presentHouseNo">
                  {t("accountForm.fields.houseNo")}
                </label>
                <input
                  id="presentHouseNo"
                  name="houseNo"
                  className={styles.input}
                  value={formState.presentAddress.houseNo}
                  onChange={(event) => updateAddress("presentAddress", event)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="presentRoadNo">
                  {t("accountForm.fields.roadNo")}
                </label>
                <input
                  id="presentRoadNo"
                  name="roadNo"
                  className={styles.input}
                  value={formState.presentAddress.roadNo}
                  onChange={(event) => updateAddress("presentAddress", event)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="presentVillageName">
                  {t("accountForm.fields.villageName")}
                </label>
                <input
                  id="presentVillageName"
                  name="villageName"
                  className={styles.input}
                  value={formState.presentAddress.villageName}
                  onChange={(event) => updateAddress("presentAddress", event)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="presentPostCode">
                  {t("accountForm.fields.postCode")}
                </label>
                <input
                  id="presentPostCode"
                  name="postCode"
                  className={styles.input}
                  value={formState.presentAddress.postCode}
                  onChange={(event) => updateAddress("presentAddress", event)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="presentDistrictId">
                  {t("accountForm.fields.district")}
                </label>
                <select
                  id="presentDistrictId"
                  name="districtId"
                  className={styles.select}
                  value={formState.presentAddress.districtId}
                  onChange={(event) => updateAddress("presentAddress", event)}
                >
                    <option value="">
                      {t("accountForm.placeholders.selectDistrict")}
                    </option>
                  {lookups.districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="presentUpazilaId">
                  {t("accountForm.fields.upazila")}
                </label>
                <select
                  id="presentUpazilaId"
                  name="upazilaId"
                  className={styles.select}
                  value={formState.presentAddress.upazilaId}
                  onChange={(event) => updateAddress("presentAddress", event)}
                  disabled={!formState.presentAddress.districtId}
                >
                    <option value="">
                      {t("accountForm.placeholders.selectUpazila")}
                    </option>
                  {presentUpazilaOptions.map((upazila) => (
                    <option key={upazila.id} value={upazila.id}>
                      {upazila.name}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                  {t("accountForm.sections.permanent")}
                </h2>
                <label className={styles.sameAsLabel} htmlFor="sameAsPresent">
                  <input
                    id="sameAsPresent"
                    type="checkbox"
                    checked={formState.sameAsPresent}
                    onChange={handleSameAsPresentToggle}
                  />
                  <span>{t("accountForm.fields.sameAsPresent")}</span>
                </label>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="permanentFlatNo">
                  {t("accountForm.fields.flatNo")}
                </label>
                <input
                  id="permanentFlatNo"
                  name="flatNo"
                  className={styles.input}
                  value={formState.permanentAddress.flatNo}
                  onChange={(event) => updateAddress("permanentAddress", event)}
                  disabled={formState.sameAsPresent}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="permanentHouseNo">
                  {t("accountForm.fields.houseNo")}
                </label>
                <input
                  id="permanentHouseNo"
                  name="houseNo"
                  className={styles.input}
                  value={formState.permanentAddress.houseNo}
                  onChange={(event) => updateAddress("permanentAddress", event)}
                  disabled={formState.sameAsPresent}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="permanentRoadNo">
                  {t("accountForm.fields.roadNo")}
                </label>
                <input
                  id="permanentRoadNo"
                  name="roadNo"
                  className={styles.input}
                  value={formState.permanentAddress.roadNo}
                  onChange={(event) => updateAddress("permanentAddress", event)}
                  disabled={formState.sameAsPresent}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="permanentVillageName">
                  {t("accountForm.fields.villageName")}
                </label>
                <input
                  id="permanentVillageName"
                  name="villageName"
                  className={styles.input}
                  value={formState.permanentAddress.villageName}
                  onChange={(event) => updateAddress("permanentAddress", event)}
                  disabled={formState.sameAsPresent}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="permanentPostCode">
                  {t("accountForm.fields.postCode")}
                </label>
                <input
                  id="permanentPostCode"
                  name="postCode"
                  className={styles.input}
                  value={formState.permanentAddress.postCode}
                  onChange={(event) => updateAddress("permanentAddress", event)}
                  disabled={formState.sameAsPresent}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="permanentDistrictId">
                  {t("accountForm.fields.district")}
                </label>
                <select
                  id="permanentDistrictId"
                  name="districtId"
                  className={styles.select}
                  value={formState.permanentAddress.districtId}
                  onChange={(event) => updateAddress("permanentAddress", event)}
                  disabled={formState.sameAsPresent}
                >
                    <option value="">
                      {t("accountForm.placeholders.selectDistrict")}
                    </option>
                  {lookups.districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="permanentUpazilaId">
                  {t("accountForm.fields.upazila")}
                </label>
                <select
                  id="permanentUpazilaId"
                  name="upazilaId"
                  className={styles.select}
                  value={formState.permanentAddress.upazilaId}
                  onChange={(event) => updateAddress("permanentAddress", event)}
                  disabled={
                    formState.sameAsPresent || !formState.permanentAddress.districtId
                  }
                >
                    <option value="">
                      {t("accountForm.placeholders.selectUpazila")}
                    </option>
                  {permanentUpazilaOptions.map((upazila) => (
                    <option key={upazila.id} value={upazila.id}>
                      {upazila.name}
                    </option>
                  ))}
                </select>
              </div>
            </section>
          </fieldset>

          <div className={styles.formActions}>
            {isEditMode && (
              <button
                type="button"
                className={styles.dangerButton}
                onClick={openDeleteConfirm}
                disabled={deleting || saving || loading || !!loadError}
              >
                {deleteButtonLabel}
              </button>
            )}
            <div className={styles.formActionsRight}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBackNavigation}
                disabled={saving}
              >
                {cancelLabel}
              </button>
              <button
                type="submit"
                className={styles.primaryButton}
                disabled={formDisabled}
              >
                {primaryActionLabel}
              </button>
            </div>
          </div>
        </form>
        <ConfirmDialog
          open={confirmDeleteOpen}
          title={t("accountForm.buttons.delete")}
          message={deleteConfirmMessage}
          confirmLabel={deleteButtonLabel}
          cancelLabel={cancelLabel}
          loading={deleting}
          onConfirm={confirmDelete}
          onCancel={closeDeleteConfirm}
        />
      </Card>
    </div>
  );
}

export default AccountFormPage;
